import React from "react";
import {
  Typography,
  Divider,
  Box,
  Paper,
  Avatar,
} from "@mui/material";



export default function Article2(props: any) {

  return (
    <div>
      <title>From Undrafted to Unhittable: Trey Gibson's Bet on Himself Leads to Top 100 Prospect Status</title>
      <Typography
        variant="h4"
        component="h1"
        style={{
          fontWeight: "bold",
          color: "#000000",
          textAlign: "center",
          fontSize: props.isDesktop ? "32pt" : "20pt",
        }}
      >
        From Undrafted to Unhittable: Trey Gibson's Bet on Himself Leads to Top 100 Prospect Status
      </Typography>
      <Typography
        variant="h6"
        style={{
          fontWeight: "bold",
          marginBottom: "12px",
          color: "rgba(0, 0, 0, 0.7)",
          textAlign: "center",
          fontSize: props.isDesktop ? "18pt" : "14pt",
        }}
      >
        By Duke Hill • August 24th, 2025
      </Typography>
      <Divider variant="middle" style={{ marginBottom: "20px" }} />


      {/* Article Content */}
      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        Orioles pitching prospect Trey Gibson recently had his first start at the Triple-A level for the Norfolk Tides: a dominant 5 inning, 1 hit, 6 strikeout performance against the Jacksonville Jumbo Shrimp. His numbers immediately popped in <a href="/player/694346">my psStuff+ model</a>. I had heard rumblings of his rise through the Orioles' system, tales of an overpowering approach from the former undrafted free agent, so I had to head to Norfolk to confirm what the data was telling me.
      </Typography>

      <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "100%" }}>
        <a href="/player/694346">
          <img src={require('../images/GibsonStats.png')} alt="loading..." style={{ maxWidth: "90%", border: "#293241 solid 2px" }} /></a>
      </div>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        The humidity in Norfolk was thick enough to chew, a familiar feeling for any Virginian who grew up with summer nights at the ballpark. For Trey Gibson, who is local to the area and grew up going to Tides games, it's the air of his childhood dreams. For me, who also attended innumerable games at Harbor Park as a kid, it's the backdrop for my first professional assignment with media credentials. We're both having a "first" here this week in the same park we both learned to love the game of baseball; his just happens to be on the mound, one step from the major leagues.
      </Typography>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        Gibson's call-up to Triple-A isn't just another roster move. It's the culmination of one of the most improbable and analytically fascinating ascents in the Orioles' system. He is the ultimate testament to modern player development: an undrafted free agent whose raw talent, once unlocked in the Orioles, has produced the 2nd most strikeouts in all of minor league baseball this season.
      </Typography>

      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, mb: 2 }}>
        <Typography variant="h4" component="h2">
          An Unexpected Detour
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        Gibson's path was nearly derailed before it truly began. As a junior at Liberty University in 2023, he was suspended from the team under circumstances the university has never publicly clarified. Given Liberty's documented history of disciplinary actions widely criticized as discriminatory and unreasonable, the legitimacy of the suspension is a valid question - especially considering what Gibson did next.
      </Typography>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        Faced with a crossroads, Gibson made a bold choice. Instead of returning to Liberty for his senior year to rebuild his draft stock, he bet on himself. Gibson chose to leave Liberty, despite the school welcoming him back for his senior year, and instead pitched in the Cape Cod League, where he was teammates with some other phenom prospects in JJ Wetherholt and Carson Benge. Even Trey himself admits his summer league results (a 10.24 ERA) were less than ideal, but he still chose to enter the professional pool as a free agent rather than return to Liberty.
      </Typography>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        The Orioles saw past the Cape Cod ERA. They saw elite spin characteristics, a devastating breaking ball, and a projectable frame. In August 2023, they signed him, offering a lifeline and a challenge.
      </Typography>

      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, mb: 2 }}>
        <Typography variant="h4" component="h2">
          The Pitching Lab Transformation
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        Upon entering the Orioles organization, Gibson was plugged into what he calls a "detailed plan" and a "solid foundation." The organization's pitching development machine, led by figures like AA pitching coach Jordie Henry, Orioles director of pitching performance Forrest Herrmann, Orioles Minor League Assistant Pitching Coordinator Adam Schuck, and AAA pitching coach Justin Ramsey, went to work.
      </Typography>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        As Gibson revealed on the <a href="https://steveonbaseball.substack.com/p/todays-guest-double-a-rhp-trey-gibson">Steve on Baseball podcast</a>, this wasn't just about tweaking a pitch or two; it was an overhaul in many ways. The Orioles' focus was on getting Gibson to develop multiple breaking pitches - he settled at four. "I've always been able to spin the baseball well, but this year has been the first year where I've really put four pitches together like that," Gibson told Steve Melewski. This wasn't guesswork, it was a targeted, data-driven mission to weaponize his pitching abilities.
      </Typography>

      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, mb: 2 }}>
        <Typography variant="h4" component="h2">
          The Arsenal
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        This season, Gibson is throwing six different pitches:
      </Typography>
      <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "100%" }}>
        <img src={require('../images/GibsonPitches.png')} alt="loading..." style={{ maxWidth: "90%", border: "#293241 solid 2px" }} />

      </div>
      <Box component="ul" sx={{ pl: 4, mb: 3 }}>
        <li><Typography variant="body1" sx={{ fontSize: '1.1rem' }}>A mid 90s 4-seamer that can touch high 90s</Typography></li>
        <li><Typography variant="body1" sx={{ fontSize: '1.1rem' }}>A (newly added, more on that later) sinker around the same velocity with some extra arm side run</Typography></li>
        <li><Typography variant="body1" sx={{ fontSize: '1.1rem' }}>A devastating mid 80s slider that tunnels well with his fastball(s)</Typography></li>
        <li><Typography variant="body1" sx={{ fontSize: '1.1rem' }}>A high 80s gyro-slider/cutter, which Gibson described to me as a nasty offering he's comfortable throwing to hitters on both sides</Typography></li>
        <li><Typography variant="body1" sx={{ fontSize: '1.1rem' }}>A low 80s curveball with massive drop</Typography></li>
        <li><Typography variant="body1" sx={{ fontSize: '1.1rem' }}>And an intriguing high 80s "death ball", a breaking pitch with around 4 inches of negative induced vertical break that dives viciously beneath bats, inspired by Jordan Montgomery's signature offering</Typography></li>
      </Box>

      <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "90%" }}>
        <img src={require('../images/GibsonOverlay.gif')} alt="loading..." style={{ maxWidth: "90%", border: "#293241 solid 2px" }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 0.5,
            fontStyle: 'italic',
            fontSize: '0.75rem'
          }}
        >
          Video Sources: MLB TV
        </Typography>
      </div>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        He showed off all 6 of these offerings in his Harbor Park debut, relentlessly attacking hitters in the zone and challenging them with his best stuff. The pitch overlay tells the story of what makes this possible: pitch tunneling. Despite Gibson's huge variety of pitches, many of them look VERY similar coming out of his hand, even when they're halfway to the plate. This forces hitters to make a quick guess of what's actually on the way, and there's a good chance they're going to guess incorrectly.
      </Typography>
      <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "100%" }}>
        <img src={require('../images/Tunnel1.png')} alt="loading..." style={{ maxWidth: "50%", border: "#293241 solid 2px" }} />
        <img src={require('../images/Tunnel2.png')} alt="loading..." style={{ maxWidth: "50%", border: "#293241 solid 2px" }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 0.5,
            fontStyle: 'italic',
            fontSize: '0.75rem'
          }}
        >
          Source: <a href="https://baseballsavant.mlb.com/gamefeed?date=8/20/2025&gamePk=780803&chartType=pitch&legendType=pitchResult&playerType=pitcher&inning=&count=&pitchHand=&batSide=&descFilter=Swinging%20Strike&ptFilter=FF,SI,FC,SL&resultFilter=&hf=pitch3d&sportId=11&liveAb=">Baseball Savant</a>
        </Typography>
      </div>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        Many scouting reports on Gibson also listed a changeup as part of his arsenal, but I noticed it was absent in his first AAA start. So, I gave Trey a break from the local media asking him how it felt to be at home and shifted his media scrum to arsenal questions.
      </Typography>

      {/* Interview Section */}
      <Paper elevation={2} sx={{ p: 3, my: 4, bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="body1" paragraph>
          <strong>Duke Hill:</strong> "More of a technical question. No changeups in your first (AAA) start - are you considering your cutter to be more of your main off-speed offering?"
        </Typography>
        <Typography variant="body1">
          <strong>Trey Gibson:</strong> "I threw the changeup last year. It was a pretty decent pitch. The Orioles liked it, but in the offseason I was coming up with a new breaking pitch. It's kind of been leaning on that so far this year, trying to get reps in. The changeup is something that's always been in the back pocket, but I just haven't felt the need to use it so far this year."
        </Typography>
      </Paper>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        So, there is indeed a SEVENTH pitch buried in Gibson’s arsenal that may make its return further down the line if he needs a new tool to fight the third time through the order.
      </Typography>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        When asked to describe himself on the mound, his answer was simple: “I’m a powerful guy. I’m gonna try to attack every hitter that steps in the box with my best stuff. Basically try to go right after them.” Baseball Savant’s Pitch 3D summary of his start makes that evident - Gibson will pound the zone and challenge hitters to put his stuff in play.
      </Typography>

      <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "100%" }}>
        <img src={require('../images/GibsonZone.png')} alt="loading..." style={{ maxWidth: "95%", border: "#293241 solid 2px" }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 0.5,
            fontStyle: 'italic',
            fontSize: '0.75rem'
          }}
        >
          Source: <a href="https://baseballsavant.mlb.com/gamefeed?date=8/20/2025&gamePk=780803&chartType=pitch&legendType=pitchName&playerType=pitcher&inning=&count=&pitchHand=&batSide=&descFilter=&ptFilter=&resultFilter=&hf=pitch3d&sportId=11&liveAb=">Baseball Savant</a>
        </Typography>
      </div>


      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        Any good power pitching style is going to require a solid fastball or two: Gibson throws three. It was clear from his usage numbers that he is becoming more comfortable with his sinker, so I asked him about it.
      </Typography>

      <Paper elevation={2} sx={{ p: 3, my: 4, bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="body1" paragraph>
          <strong>Duke Hill:</strong> "More on the power pitching style: so you're throwing three different fastballs, would you say that you would consider one of them to be your best? It seems like you're throwing the sinker more than the 4-seam?"
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Trey Gibson:</strong> "Yeah, so the sinker is new, about two months ago."
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Duke Hill:</strong> "It's grading out amazingly."
        </Typography>
        <Typography variant="body1" >
          <strong>Trey Gibson:</strong> "Yeah, so it had a lot of really early AB's end in like just weak swings from righties. Only throw it to righties so far. I think the 4-seam plays better to lefties, but I think my cutter - it's a pretty nasty pitch, so I've (thrown it to) both sides, like using it."
        </Typography>
      </Paper>

      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, mb: 2 }}>
        <Typography variant="h4" component="h2">
          By The Numbers: A Model of Dominance
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        The results of Gibson’s transformation are staggering. As I mentioned earlier, his 147 MiLB strikeouts on the season are 2nd in all of affiliated ball, only behind Jonah Tong. While he may have started the season with a 5.12 ERA over 38.2 IP in High-A Aberdeen, that number masks an ABSURD 15.59 K/9.
      </Typography>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        From a runs perspective, Gibson’s true breakout came at Double-A Chesapeake, where he ran a 1.55 ERA over 52.1 innings. I was curious whether or not Trey considered his immaculate Double-A ERA to be his true “breakout”, or if his focus would land on his nearly 16 strikeouts per 9 innings in Aberdeen, so I asked.
      </Typography>

      <Paper elevation={2} sx={{ p: 3, my: 4, bgcolor: 'primary.light', color: 'white' }}>
        <Typography variant="body1" paragraph>
          <strong>Duke Hill:</strong> "Looking at your statline and game logs, it seems like you turned a big corner when you got to Chesapeake in AA. Is there a specific adjustment that you made that contributed to that, or was it just the result of the Orioles’ development plan in general?"
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Trey Gibson:</strong> "I can't really focus on one exact thing. I feel like this entire year has been just a byproduct of two years of buying into the program. Because I think I was striking out a decent clip of batters in High-A (Aberdeen) too. It was just, maybe some bad luck, whatever. But I think once I got to Bowie (Chesapeake), I was pretty familiar with the pitching coach there. So it was already hitting the ground running, and he kind of already knows my plan well. So it's kind of a good mix of people."
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Duke Hill:</strong> "People say that's a big jump from High-A to Double-A."
        </Typography>
        <Typography variant="body1" >
          <strong>Trey Gibson:</strong> "Yeah, that was the first time where I'd noticed those hitters having those (see below) approaches that became glaring, Double-A."
        </Typography>
      </Paper>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        Earlier in the scrum Gibson emphasized noticing how the biggest changes he saw when moving up the levels was the quality of each hitter’s plate approach, noting “I think each hitter in every lineup now has their their own approach, and they're going to spit on balls that you think are good pitches, that they just don't want have anything to do with. So I feel like now being up here, trying to fight through a lineup, that third time through, it is going to be really, really something I'm gonna look forward to.” More evidence of what I’ve been emphasizing through this breakdown - Gibson revels in the challenge of attacking hitters head on.
      </Typography>


      {/* Section Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, mb: 2 }}>
        <Typography variant="h4" component="h2">
          Homecoming
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
        At his introductory media scrum in Norfolk, the local kid in him was evident. "I think I probably went to about 15 [games] here with my family," he said, grinning when asked about his childhood visits to Harbor Park. Gibson was asked by the local media if he imagined pitching in Harbor Park one day and responded confidently - "Definitely. It's definitely, you know, you fall asleep as a kid dreaming about baseball. It's like, hopefully I get to play here one day." He also spoke about the boost of having family and friends in the stands and the surreal feeling of being on the doorstep of the show.
      </Typography>

      <Typography variant="body1" paragraph sx={{ fontSize: '1.2rem', mt: 4, fontWeight: 'bold' }}>
        The day after our conversation in Norfolk, Gibson’s bet on himself reached its highest validation yet: he was officially named to <a href="https://www.espn.com/mlb/story/_/id/46017620/2025-mlb-prospect-rankings-update-kiley-mcdaniel-konnor-griffin-kevin-mcgonigle-jesus-made">ESPN’s Top 100 Prospects list</a>. Trey Gibson's journey has been unconventional, resilient, and data-driven - a better story than any straight-line success. It's a story about second chances, the power of a well laid out development plan, and the undeniable excitement of betting on yourself to achieve your dream. Only now, Trey Gibson isn't in the stands dreaming. He's on the field, making everyone else dream of what he might do next.
      </Typography>
      {/* Author Bio */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 5, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>DH</Avatar>
        <Box>
          <Typography variant="h6">By Duke Hill</Typography>
          <Typography variant="body2" color="text.secondary">
            Creator and Editor in Chief of Prospect Savant
          </Typography>
        </Box>
      </Box>


      {/* Final Section */}
      <Box sx={{ mt: 5, p: 3, bgcolor: 'grey.100', borderRadius: 2 }}>
        <Typography variant="h5" component="h3" gutterBottom>
          A Personal Parallel
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
          I was originally at Harbor Park on Tuesday to simply observe Gibson's start from the press box, without any thought that I would get the chance to speak with him on his big day. That changed when the game was ultimately rained out and canceled, and I was invited to join my first ever media scrum with about 10 minutes to prepare. I spent the first few minutes listening to the local reporters from the new outlets I watched growing up in Norfolk, gauging the flow of the scrum before finding the right moment to ask a more technical question. But watching Gibson handle questions with a quiet, focused confidence, filled me with some confidence of my own. I couldn't help but see the parallels. We are both 23. We both grew up with the crack of the bat and the roar of the crowd at Harbor Park as the soundtrack of our summers. Heck, we may have even faced off as kids in the southeast Virginia Little League circuit. This week, we both achieved a personal milestone here: his first home start only one promotion away from MLB, my first credential around my neck.
        </Typography>
        <div style={{ textAlign: "center", margin: "20px 0", maxWidth: "100%" }}>
          <img src={require('../images/GibsonScrum.png')} alt="loading..." style={{ maxWidth: "95%", border: "#293241 solid 2px" }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 0.5,
              fontStyle: 'italic',
              fontSize: '0.75rem'
            }}
          >
            Source: <a href="https://www.pilotonline.com/author/billy-schuerman/">Billy Schuerman</a> (thanks Billy!)
          </Typography>
        </div>
      </Box>



      
    </div>
  );
}
